import { squaresFor } from '../grid/measure.js';
export const WEATHER_KINDS = ['weather.set'];
export const WEATHER_LIMITS = { conditions: 24, label: 60, notes: 120, distance: 10000, squares: 1000 };
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const DIAGONALS = ['alternate', 'one', 'two', 'straight'];
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
const isText = (v, max) => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
const isDistance = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= WEATHER_LIMITS.distance;
/**
 * `features.combat.weather`, strictly: a list of conditions, keys unique, each sight rule well formed and each concealment
 * one of `concealments` (the system's keys). Anything malformed is none — nothing defaulted.
 */
export function parseWeatherRules(v, concealments) {
    if (!Array.isArray(v) || v.length > WEATHER_LIMITS.conditions)
        return [];
    const out = [];
    for (const e of v) {
        if (!isObj(e) || !isKey(e.key) || !isText(e.label, WEATHER_LIMITS.label))
            return [];
        if (e.notes !== undefined && !isText(e.notes, WEATHER_LIMITS.notes))
            return [];
        let limit = null;
        const concealment = [];
        if (e.sight !== undefined) {
            const s = e.sight;
            if (!isObj(s) || !('limit' in s) || Object.keys(s).some((k) => k !== 'limit' && k !== 'concealment'))
                return [];
            if (s.limit !== null && !(isDistance(s.limit) && s.limit > 0))
                return [];
            limit = s.limit;
            if (s.concealment !== undefined) {
                if (!Array.isArray(s.concealment) || !s.concealment.length || s.concealment.length > WEATHER_LIMITS.conditions)
                    return [];
                for (const c of s.concealment) {
                    if (!isObj(c) || !isDistance(c.from) || (limit !== null && c.from > limit) || !isKey(c.concealment) || !concealments.includes(c.concealment))
                        return [];
                    concealment.push({ from: c.from, concealment: c.concealment });
                }
            }
        }
        out.push({ key: e.key, label: e.label.trim(), notes: typeof e.notes === 'string' ? e.notes.trim() : null, limit, concealment });
    }
    return new Set(out.map((w) => w.key)).size === out.length ? out : [];
}
/** A stamped weather, strictly; null when malformed. */
export function parseWeather(v) {
    if (!isObj(v) || !isKey(v.key) || !isText(v.label, WEATHER_LIMITS.label))
        return null;
    const sight = v.sight;
    if (sight !== null && !(typeof sight === 'number' && Number.isFinite(sight) && sight > 0 && sight <= WEATHER_LIMITS.squares))
        return null;
    const diagonal = v.diagonal;
    if (diagonal !== null && !DIAGONALS.includes(diagonal))
        return null;
    // A limit is counted by a diagonal rule: one without the other is not a weather the table can apply.
    if ((sight === null) !== (diagonal === null))
        return null;
    return { key: v.key, label: v.label, sight: sight, diagonal: diagonal };
}
export function parseWeatherBody(b) {
    if (b.kind !== 'weather.set')
        return null;
    if (b.key !== null && !isKey(b.key))
        return null;
    if (b.weather === undefined)
        return { kind: 'weather.set', key: b.key };
    const w = parseWeather(b.weather);
    return w && b.key !== null && w.key === b.key ? { kind: 'weather.set', key: b.key, weather: w } : null;
}
export const normaliseWeather = (v) => (v == null ? null : parseWeather(v));
export const copyWeather = (w) => (w ? { ...w } : null);
/** The scene's weather after an accepted event: the stamp, or none. */
export const reduceWeather = (body) => (body.key === null ? null : copyWeather(body.weather));
export const NO_WEATHER = "This table's game system has no weather conditions.";
export const UNKNOWN_WEATHER = 'That is not a weather condition of this game system.';
/**
 * The server's stamp for `key` from the table's rules: the condition's label, its limit in squares by `per_square`, and
 * the diagonal rule; a string (the refusal, in words) for a key the system lacks. Null clears.
 */
export function stampWeather(key, rules, distance) {
    if (key === null)
        return null;
    if (!rules.length)
        return NO_WEATHER;
    const rule = rules.find((r) => r.key === key);
    if (!rule)
        return UNKNOWN_WEATHER;
    // A limit needs the distance rule to become squares (the validator requires it); without one, it limits nothing.
    if (rule.limit === null || !distance)
        return { key: rule.key, label: rule.label, sight: null, diagonal: null };
    const squares = Math.min(WEATHER_LIMITS.squares, rule.limit / distance.perSquare);
    return { key: rule.key, label: rule.label, sight: squares, diagonal: distance.diagonal };
}
/** The squares between a footprint (`i0..i1`, `j0..j1`) and square (i, j), nearest square to it, by the diagonal rule. */
export function footprintReach(fp, i, j, diagonal) {
    const di = Math.max(0, fp.i0 - i, i - fp.i1);
    const dj = Math.max(0, fp.j0 - j, j - fp.j1);
    return squaresFor(di, dj, diagonal);
}
/** Whether a square is within the weather's sight of a footprint: always with no limit. */
export function withinWeather(w, fp, i, j) {
    if (!w || w.sight === null || w.diagonal === null)
        return true;
    return footprintReach(fp, i, j, w.diagonal) <= w.sight + 1e-9;
}
/** The key sight is carried by (§2.1): a weather change recomputes every eye; its label changes nobody's sight. */
export const weatherKey = (w) => (w && w.sight !== null ? `${w.sight}|${w.diagonal}` : '');
export const TIMES_OF_DAY = [
    { key: 'day', label: 'Day', level: 'bright' },
    { key: 'dusk', label: 'Dawn or dusk', level: 'dim' },
    { key: 'night', label: 'Night', level: 'dark' },
];
export const timeOfDay = (level) => TIMES_OF_DAY.find((t) => t.level === (level ?? 'bright')).key;
//# sourceMappingURL=weather.js.map